import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supplierService } from '../../services/supplierService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';

const Entities = () => {
  const canMutate = useCanMutate();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEntities, setLoadingEntities] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadEntities();
    }
  }, []);

  const loadEntities = async () => {
    setLoadingEntities(true);
    try {
      const response = await supplierService.getSuppliers();
      if (response.success && response.data) {
        setEntities(response.data);
      } else {
        setToast({ isOpen: true, message: 'Error al cargar entidades', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar entidades',
        type: 'error',
      });
    } finally {
      setLoadingEntities(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!entityToDelete) return;
    setLoading(true);
    try {
      const response = await supplierService.deleteSupplier(entityToDelete.id);
      if (response.success) {
        await loadEntities();
        setIsDeleteModalOpen(false);
        setEntityToDelete(null);
        setToast({ isOpen: true, message: 'Entidad eliminada exitosamente', type: 'success' });
      } else {
        throw new Error(response.message || 'Error al eliminar entidad');
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar entidad',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const filtered = entities.filter((entity) => {
    const docNumber = entity.document_number || '';
    const matchesSearch =
      !searchTerm ||
      (entity.code && entity.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entity.name && entity.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (docNumber && docNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entity.email && entity.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && entity.is_active) ||
      (statusFilter === 'inactive' && !entity.is_active);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Entidades</h1>
              <p className="text-gray-600 mt-1">Gestión general de proveedores, clientes e internos</p>
            </div>
            <MutateGate>
              <Link to="/entities/create" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium">
                + Nueva Entidad
              </Link>
            </MutateGate>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
            <input
              type="text"
              placeholder="Buscar por código, nombre, documento o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {loadingEntities ? (
          <div className="text-center py-12 text-gray-500">Cargando entidades...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">No se encontraron entidades</div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-x-auto min-w-0">
            <table className="w-full min-w-[52rem] table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-[7rem] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="w-[11rem] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
                  <th className="w-[9rem] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipos</th>
                  <th className="w-[6.5rem] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="w-[9.5rem] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((entity) => (
                  <tr key={entity.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-teal-700">{entity.code}</td>
                    <td className="px-4 py-4 text-sm text-gray-900 max-w-0">
                      <span className="block truncate" title={entity.name || ''}>{entity.name}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span className="block truncate" title={entity.document_number || ''}>
                        {entity.document_type === 'dni' ? 'DNI' : entity.document_type === 'other' ? 'Otro' : 'RUC'}: {entity.document_number || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {entity.is_provider ? <span className="px-2 py-1 text-xs rounded-full bg-teal-100 text-teal-800">Proveedor</span> : null}
                        {entity.is_client ? <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Cliente</span> : null}
                        {entity.is_internal ? <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">Interno</span> : null}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${entity.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {entity.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <Link to={`/entities/${entity.id}/edit`} className="text-purple-600 hover:text-purple-900 mr-4">
                        {canMutate ? 'Editar' : 'Ver'}
                      </Link>
                      <MutateGate>
                        <button
                          type="button"
                          onClick={() => {
                            setEntityToDelete(entity);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </MutateGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setEntityToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Entidad"
        message={`¿Estás seguro de que deseas eliminar la entidad "${entityToDelete?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={loading}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Entities;

